/**
 * To-Do tab inside job detail — shows to-do items linked to this job.
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchTodos, createTodo, updateTodo, toggleTodo, deleteTodo } from '../../../api/todos';
import { Button, LoadingSpinner, EmptyState } from '../../common';
import { useAuth } from '../../../context/AuthContext';
import client from '../../../api/client';

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return d; }
}

const PRIORITY_STYLES = {
  high: 'bg-red-100 text-red-700',
  normal: 'bg-gray-100 text-gray-600',
  low: 'bg-blue-100 text-blue-600',
};

export default function TodoTab({ job }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);

  // Add form
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', description: '', priority: 'normal', due_date: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  // Edit form
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      const params = { job: job.id, page_size: 200 };
      if (!showCompleted) params.is_completed = false;
      const resp = await fetchTodos(params);
      setItems(resp.data || resp.results || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [job.id, showCompleted]);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => {
    client.get('/auth/users/').then(({ data }) => {
      setAllUsers(data.data || data.results || data || []);
    }).catch(() => {});
  }, []);

  async function handleToggle(id) {
    try {
      await toggleTodo(id);
      loadItems();
    } catch { /* ignore */ }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this to-do item?')) return;
    try { await deleteTodo(id); loadItems(); }
    catch { /* ignore */ }
  }

  function startEdit(item) {
    setEditingId(item.id);
    setEditForm({
      title: item.title || '',
      description: item.description || '',
      priority: item.priority || 'normal',
      due_date: item.due_date || '',
      assigned_to: item.assigned_to || '',
    });
  }

  async function handleSaveEdit() {
    if (!editForm.title.trim()) { alert('Title is required.'); return; }
    setEditSaving(true);
    try {
      await updateTodo(editingId, {
        title: editForm.title.trim(),
        description: editForm.description.trim(),
        priority: editForm.priority,
        due_date: editForm.due_date || null,
        assigned_to: editForm.assigned_to || undefined,
      });
      setEditingId(null);
      loadItems();
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.error || 'Save failed');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleAdd() {
    if (!addForm.title.trim()) { alert('Title is required.'); return; }
    setSaving(true);
    try {
      await createTodo({
        title: addForm.title.trim(),
        description: addForm.description.trim(),
        priority: addForm.priority,
        due_date: addForm.due_date || null,
        job: job.id,
        assigned_to: addForm.assigned_to || user.id,
      });
      setAddForm({ title: '', description: '', priority: 'normal', due_date: '', assigned_to: '' });
      setAddOpen(false);
      loadItems();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Failed to create to-do');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="flex justify-center py-10"><LoadingSpinner /></div>;

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Toolbar */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {items.filter((i) => !i.is_completed).length} active to-do{items.filter((i) => !i.is_completed).length !== 1 ? 's' : ''}
          </span>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="rounded border-gray-300 text-blue-600"
            />
            Show completed
          </label>
        </div>
        <Button size="sm" onClick={() => setAddOpen(!addOpen)}>
          {addOpen ? 'Cancel' : '+ Add To-Do'}
        </Button>
      </div>

      {/* Add form */}
      {addOpen && (
        <div className="px-4 py-3 border-b border-gray-100 bg-blue-50/30 space-y-2">
          <input
            type="text"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="What needs to be done for this job?"
            value={addForm.title}
            onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAdd()}
          />
          <textarea
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            rows={2}
            placeholder="Details (optional)"
            value={addForm.description}
            onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
          />
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Priority</label>
              <select className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={addForm.priority} onChange={(e) => setAddForm({ ...addForm, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Due Date</label>
              <input type="date" className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={addForm.due_date} onChange={(e) => setAddForm({ ...addForm, due_date: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Assign To</label>
              <select className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={addForm.assigned_to} onChange={(e) => setAddForm({ ...addForm, assigned_to: e.target.value })}>
                <option value="">Myself</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</option>
                ))}
              </select>
            </div>
            <Button size="sm" loading={saving} onClick={handleAdd}>Add</Button>
            <Button size="sm" variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Items */}
      {items.length === 0 ? (
        <div className="px-4 py-8">
          <EmptyState title="No to-do items" description="Click '+ Add To-Do' to create a task for this job." />
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.map((item) => {
            const isOverdue = !item.is_completed && item.due_date && new Date(item.due_date + 'T23:59:59') < new Date();
            const isCreatedByMe = item.created_by === user?.id;
            const isEditing = editingId === item.id;

            if (isEditing) {
              return (
                <div key={item.id} className="px-4 py-3 bg-blue-50/50 space-y-2">
                  <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-medium" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} autoFocus />
                  <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" rows={2} placeholder="Details (optional)" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                  <div className="flex items-end gap-3 flex-wrap">
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Priority</label>
                      <select className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}>
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Due Date</label>
                      <input type="date" className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-0.5">Assign To</label>
                      <select className="border border-gray-300 rounded-md px-2 py-1.5 text-sm" value={editForm.assigned_to} onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })}>
                        <option value="">Myself</option>
                        {allUsers.map((u) => (
                          <option key={u.id} value={u.id}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</option>
                        ))}
                      </select>
                    </div>
                    <Button size="sm" loading={editSaving} onClick={handleSaveEdit}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                </div>
              );
            }

            return (
              <div key={item.id} className={`px-4 py-2.5 flex items-start gap-3 ${item.is_completed ? 'opacity-50' : isOverdue ? 'bg-red-50/30' : ''}`}>
                <button
                  onClick={() => handleToggle(item.id)}
                  className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    item.is_completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-blue-500'
                  }`}
                >
                  {item.is_completed && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${item.is_completed ? 'line-through text-gray-400' : 'text-foreground'}`}>{item.title}</span>
                    {item.priority !== 'normal' && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${PRIORITY_STYLES[item.priority]}`}>{item.priority}</span>
                    )}
                    {item.due_date && (
                      <span className={`text-xs ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                        {isOverdue ? '⚠ ' : ''}Due {fmtDate(item.due_date)}
                      </span>
                    )}
                  </div>
                  {item.description && <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>}
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                    {item.assigned_to !== user?.id && item.assigned_to_display && <span>→ {item.assigned_to_display.name}</span>}
                    {item.created_by !== user?.id && item.created_by_display && <span>from {item.created_by_display.name}</span>}
                  </div>
                </div>
                {!item.is_completed && (
                  <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                    {isCreatedByMe && (
                      <button onClick={() => startEdit(item)} className="text-gray-300 hover:text-blue-500" title="Edit">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                    <button onClick={() => handleDelete(item.id)} className="text-gray-300 hover:text-red-500" title="Delete">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
